import React from 'react';
import styled from 'styled-components';
import { H4 } from 'components';
import LineItem from 'components/Calculator/ToggleableBreakdown/LineItem';
import { theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import { ICategoryGroup, ICheck } from 'types';
import { buildCheckSalesItems } from 'utils';

interface ICheckDetailsProps {
  check: ICheck;
  creditCardFee?: number;
  categoryGroups?: ICategoryGroup[];
}

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 ${toRem(24)} ${toRem(18)} ${toRem(18)};
  width: 100%;
`;

const DetailsContainer = styled.div`
  display: flex;
  padding: ${toRem(16)};
  flex-direction: column;
  justify-content: center;
  gap: ${toRem(8)};
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.white};
  width: 100%;

  h4 {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

const LineItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0 0 ${toRem(16)} 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.neutral[300]};
  }

  div.total {
    p {
      font-weight: ${theme.font.weight.semiBold};
    }
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(8)};
`;

const Header = styled.p`
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const CheckDetails: React.FC<ICheckDetailsProps> = ({ check, categoryGroups, creditCardFee }) => {
  // Build Check -> Collected
  const collectedLineItems: ITipsBreakdownLineItem[] = [
    { label: 'CC Tips', amount: check.totals.tip.creditCard || 0 },
    { label: 'CC Autograts', amount: check.totals.autoGratuity.creditCard || 0 },
    ...(check.totals.tip.creditCardFee || check.totals.autoGratuity.creditCardFee
      ? [
          {
            label: 'CC Fees',
            amount: -(check.totals.tip.creditCardFee || 0) + -(check.totals.autoGratuity.creditCardFee || 0),
            ...(creditCardFee && { description: `(${creditCardFee}%)` }),
          },
        ]
      : []),
    { label: 'Cash Tips', amount: check.totals.tip.cash || 0 },
    { label: 'Cash Autograts', amount: check.totals.autoGratuity.cash || 0 },
    { label: 'Total', amount: check.totals.net },
  ];

  // Build Check -> Sales
  const salesLineItems: ITipsBreakdownLineItem[] = buildCheckSalesItems(check.categories, categoryGroups);

  return (
    <>
      <DetailsWrapper data-cy="checks-list-item-check-details">
        <DetailsContainer>
          <H4 fontWeight="medium">Check Details</H4>
          <LineItemsSection data-cy="checks-list-item-check-details-collected">
            <HeaderRow data-cy="checks-list-item-check-details-collected-header">
              <Header>Collected</Header>
              <Header>Amount</Header>
            </HeaderRow>
            {collectedLineItems?.map((lineItem, idx) => (
              <LineItem key={`line_item_collected_${idx}`} item={lineItem} dataCy="checks-list-item-check-details-collected-line-item" className={collectedLineItems.length === idx + 1 ? 'total' : ''} />
            ))}
          </LineItemsSection>
          <LineItemsSection data-cy="checks-list-item-check-details-sales">
            <HeaderRow data-cy="checks-list-item-check-details-sales-header">
              <Header>Sales</Header>
              <Header>Amount</Header>
            </HeaderRow>
            {salesLineItems?.map((lineItem, idx) => (
              <LineItem key={`line_item_sales_${idx}`} item={lineItem} dataCy="checks-list-item-check-details-sales-line-item" />
            ))}
          </LineItemsSection>
        </DetailsContainer>
      </DetailsWrapper>
    </>
  );
};

export default CheckDetails;
